<template>
  <span>
    <img :src="image" class="icon" :alt="altText" />
  </span>
</template>
<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps({
  fileName: {
    type: String,
    required: true
  },
  isDirectory: {
    type: Boolean,
    default: false
  }
})

const extensionMap = {
  word: ['doc', 'docx', 'docm'],
  text: ['dot', 'dotx', 'dotm', 'txt', 'rtf', 'sldx', 'sldm', 'do', 'odt', 'oft', 'pages', 'ttl', 'p7s'],
  cer: ['p12', 'pfx', 'cer', 'crt', 'csig', 'dsig', 'sig', 'xsig'],
  image: [
    'gif',
    'svg',
    'jpeg',
    'jpg',
    'png',
    'bmp',
    'tif',
    'tga',
    'tiff',
    'pcx',
    'pic',
    'emf',
    'ico',
    'jgw',
    'dib',
    'log',
    'wri',
    'xps',
    'heic',
    'odg',
    'jfif'
  ],
  pdf: ['pdf', 'odp'],
  excel: ['xls', 'xlsx', 'xlsm', 'xlm', 'xlt', 'xltx', 'xlsb', 'xlam', 'xlv', 'ods', 'xltm', 'csv'],
  zip: ['zip'],
  ppt: ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'ppa', 'ppam', 'pps', 'ppsx', 'ppsm'],
  mail: ['eml', 'msg'],
  rar: ['rar'],
  html: ['html', 'mhtml', 'htm', 'mht'],
  design: ['dwg', 'dxf', 'psd', 'gml'],
  url: ['url'],
  edg: ['edg'],
  video: ['mp4', 'vob', 'wmv', 'avi', 'mov', 'mpeg', 'mpg', 'divx', 'mdt', 'm4a', 'mkv', 'wmp', 'vlc', 'oga'],
  database: ['db', 'mdb'],
  vcard: ['vcf'],
  code: ['css', 'xml', 'thmx', 'xsd', 'n3', 'xmp'],
  audio: ['mp3', 'wav', 'cda', 'opus', 'ogg', 'webm'],
  zipFiles: ['7z', 'gz', 'arj']
}

const images = {
  audio: require('@/assets/images/svg/icon-video.svg'),
  cer: require('@/assets/images/svg/icon-certified-document.svg'),
  code: require('@/assets/images/svg/icon-html.svg'),
  database: require('@/assets/images/svg/icon-microsoft-folder-configuration.svg'),
  design: require('@/assets/images/svg/icon-example.svg'),
  edg: require('@/assets/images/svg/icon-money-world.svg'),
  excel: require('@/assets/images/svg/icon-microsoft-excel.svg'),
  folder: require('@/assets/images/svg/icon-microsoft-folder-closed.svg'),
  html: require('@/assets/images/svg/icon-microsoft-html.svg'),
  image: require('@/assets/images/svg/icon-microsoft-img.svg'),
  mail: require('@/assets/images/svg/icon-microsoft-mail.svg'),
  pdf: require('@/assets/images/svg/icon-microsoft-pdf.svg'),
  ppt: require('@/assets/images/svg/icon-microsoft-ppt.svg'),
  rar: require('@/assets/images/svg/icon-microsoft-folder-lock.svg'),
  test: require('@/assets/images/svg/icon-microsoft.svg'),
  text: require('@/assets/images/svg/icon-microsoft-document.svg'),
  unknow: require('@/assets/images/svg/icon-microsoft-document-break.svg'),
  url: require('@/assets/images/svg/icon-link.svg'),
  vcard: require('@/assets/images/svg/icon-microsoft-folder-user.svg'),
  video: require('@/assets/images/svg/icon-video.svg'),
  word: require('@/assets/images/svg/icon-microsoft-word.svg'),
  zip: require('@/assets/images/svg/icon-microsoft-zip.svg'),
  zipFiles: require('@/assets/images/svg/icon-microsoft-folder-zip.svg')
}

const extension = computed(() => props.fileName.split('.').pop()?.toLowerCase() || '')
const altText = computed(() => `${getExtMap.value} icon`)

const image = computed(() => {
  if (props.isDirectory) {
    return images.folder
  }
  return images[getExtMap.value as keyof typeof images] || images.unknow
})

const getExtMap = computed(() => {
  for (const [type, extensions] of Object.entries(extensionMap)) {
    if (extensions.includes(extension.value)) {
      return type
    }
  }
  return 'unknow'
})
</script>
<style lang="scss" scoped>
.icon {
  width: var(--icon-size, 24px);
  height: var(--icon-size, 24px);
}
</style>
